import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Check, X, Quote, Radio, User, Heart, GraduationCap,
  Shield, ClipboardList, PenTool, CalendarDays, Handshake, Newspaper, Brain,
  BookOpen, Users, FileText, History, BarChart3, Wand2, TrendingUp, Video,
  PenLine, MessagesSquare, Trophy, Sparkles, Globe2, Lock, Zap, Target, Repeat,
} from "lucide-react";
import { ASSETS } from "../lib/assets";
import { Overline, Reveal } from "../components/Section";

// Set to true to restore the full CricStoryLive product details page
const SHOW_DETAILS = false;

const comparison = [
  { old: "Live Scores", neu: "Live Cricket" },
  { old: "Statistics", neu: "AI Match Intelligence" },
  { old: "News", neu: "Player Stories" },
  { old: "Match Schedules", neu: "Community Engagement" },
  { old: null, neu: "Grassroots Talent" },
  { old: null, neu: "Digital Cricket Identity" },
];

const visionMoments = [
  "Every run...", "Every wicket...", "Every partnership...", "Every victory...",
  "Every defeat...", "Every debut...", "Every comeback...", "Every young cricketer...",
];

const pillars = [
  {
    i: Radio, t: "Live Cricket",
    items: ["Real-time scores", "Ball-by-ball updates", "Fixtures", "Results", "Rankings", "Tournament Coverage"],
  },
  {
    i: Brain, t: "AI Match Intelligence",
    items: ["AI Match Summaries", "Player Performance Analysis", "Predictive Insights", "AI Commentary", "Performance Trends", "Smart Match Highlights"],
  },
  {
    i: BookOpen, t: "Player Stories",
    items: ["Career Journey", "Milestones", "Achievements", "Personal Stories", "Inspirational Moments", "Success Timeline"],
  },
  {
    i: Users, t: "Community",
    items: ["Fans", "Players", "Coaches", "Academies", "Scorers", "Tournament Organizers", "Commentators", "Sponsors"],
  },
];

const grassroots = [
  "School Cricket", "College Cricket", "District Cricket", "Club Cricket",
  "Corporate Cricket", "Women's Cricket", "Grassroots Cricket", "State Associations",
  "Youth Academies", "Local Tournaments",
];

const aiFeatures = [
  { i: FileText, t: "AI Match Summary" },
  { i: User, t: "AI Player Insights" },
  { i: History, t: "AI Career Timeline" },
  { i: BarChart3, t: "AI Performance Analytics" },
  { i: Wand2, t: "AI Fantasy Assistant" },
  { i: TrendingUp, t: "AI Smart Predictions" },
  { i: Video, t: "AI Video Highlights" },
  { i: PenLine, t: "AI Story Generator" },
  { i: Newspaper, t: "AI Match Recaps" },
  { i: MessagesSquare, t: "AI Fan Engagement" },
];

const ecosystemNodes = [
  { i: Radio, t: "Live Scores" },
  { i: User, t: "Players" },
  { i: Heart, t: "Fans" },
  { i: GraduationCap, t: "Academies" },
  { i: Shield, t: "Teams" },
  { i: ClipboardList, t: "Coaches" },
  { i: PenTool, t: "Scorers" },
  { i: CalendarDays, t: "Organizers" },
  { i: Handshake, t: "Sponsors" },
  { i: Newspaper, t: "Media" },
  { i: Brain, t: "AI Insights" },
  { i: BookOpen, t: "Stories" },
  { i: Users, t: "Community" },
];

const memories = [
  "Every innings has emotions.",
  "Every player has dreams.",
  "Every partnership has trust.",
  "Every victory has sacrifice.",
  "Every defeat teaches resilience.",
  "Every run contributes to a larger story.",
];

const phases = [
  { p: "Phase 1", items: ["Live Scores", "Match Center", "Player Profiles"] },
  { p: "Phase 2", items: ["AI Match Insights", "AI Stories", "Local Cricket Coverage"] },
  { p: "Phase 3", items: ["Fantasy Cricket", "AI Fantasy Assistant", "Community Platform", "Tournament Management"] },
  { p: "Phase 4", items: ["Talent Marketplace", "Academy Network", "Coach Discovery", "Scholarship Opportunities", "Video Highlights", "Global Cricket Community"] },
];

const whyChoose = [
  { i: Brain, t: "AI Powered" }, { i: Radio, t: "Live Cricket" },
  { i: Trophy, t: "Talent Recognition" }, { i: BookOpen, t: "Player Stories" },
  { i: Users, t: "Community Driven" }, { i: Target, t: "Grassroots Focus" },
  { i: Wand2, t: "Fantasy Ready" }, { i: Sparkles, t: "Future Ready" },
  { i: Lock, t: "Privacy First" }, { i: Globe2, t: "Global Vision" },
];

const builtFor = [
  "A Fan", "A Player", "A Coach", "An Academy", "A Scorer",
  "A Tournament Organizer", "A Parent", "A Sponsor", "A Cricket Enthusiast",
];

export default function CricStoryLive() {
  useEffect(() => {
    document.title = "CricStoryLive™ — AI-Powered Digital Cricket Ecosystem | PJSRS Infotech";
    return () => { document.title = "PJSRS Infotech Private Limited"; };
  }, []);

  if (!SHOW_DETAILS) {
    return (
      <div data-testid="cricstorylive-page" className="bg-black text-white">
        <section className="relative min-h-[92vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img src={ASSETS.images.cricstoryHero} alt="" className="h-full w-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black" />
            <div className="absolute -top-32 right-1/4 h-96 w-[500px] rounded-full bg-yellow-500/15 blur-[120px]" />
          </div>
          <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-32 w-full text-center">
            <Reveal>
              <Link to="/products" data-testid="cricstory-back-products" className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white">
                ← Back to products
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 mx-auto h-40 w-40 rounded-3xl ring-1 ring-yellow-500/30 bg-black overflow-hidden grid place-items-center shadow-[0_20px_80px_rgba(212,175,55,0.25)]"
              >
                <img src={ASSETS.logos.cricstorylive} alt="CricStoryLive" className="h-full w-full object-contain" />
              </motion.div>
            </Reveal>
            <Reveal delay={0.2}>
              <Overline color="text-yellow-400" className="mt-10">AI-Powered Digital Cricket Ecosystem</Overline>
            </Reveal>
            <Reveal delay={0.25}>
              <h1 className="mt-4 font-display font-black text-[clamp(2.75rem,7vw,5rem)] leading-[0.95] tracking-tighter text-white">
                <span className="text-gradient-gold">CricStoryLive</span><sup className="text-[0.3em] font-bold align-super text-zinc-500">™</sup>
              </h1>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-6 font-display text-xl md:text-2xl font-bold text-zinc-300">
                Every Run Tells A Story.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-12 inline-flex items-center gap-3 rounded-full bg-yellow-500/10 border border-yellow-500/30 px-5 py-3 text-sm text-yellow-300 font-medium">
                <Lock className="h-4 w-4" />
                Product details coming soon
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.5}>
              <p className="mt-8 max-w-xl mx-auto text-zinc-400">
                We&apos;re crafting something special for cricket fans, players and organisers. For
                partnerships, early access or press enquiries, please get in touch.
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="mt-10 flex flex-wrap justify-center gap-3">
                <Link
                  to="/contact"
                  data-testid="cricstory-cta-contact"
                  className="inline-flex items-center gap-2 rounded-full bg-yellow-500 text-black px-7 py-4 text-sm font-bold hover:bg-yellow-400 transition shadow-[0_10px_40px_rgba(212,175,55,0.4)] hover:-translate-y-0.5"
                >
                  Get in touch <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/products"
                  data-testid="cricstory-link-products"
                  className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-7 py-4 text-sm font-medium hover:bg-white/10 transition backdrop-blur"
                >
                  <Sparkles className="h-4 w-4" /> Explore other products
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="cricstorylive-page" className="bg-black text-white">
      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ASSETS.images.cricstoryHero} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black" />
          <div className="absolute -top-32 right-1/4 h-96 w-[500px] rounded-full bg-yellow-500/15 blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Link to="/products" data-testid="cricstory-back-products" className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white">
                ← Back to products
              </Link>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="mt-6 flex items-center gap-4">
                <img src={ASSETS.logos.cricstorylive} alt="CricStoryLive" className="h-20 w-20 rounded-2xl ring-1 ring-yellow-500/20 bg-black object-contain" />
                <div>
                  <Overline color="text-yellow-400">AI-Powered Digital Cricket Ecosystem</Overline>
                  <div className="font-display text-3xl font-black mt-1 text-white">CricStoryLive<sup className="text-xs font-bold align-super text-zinc-500">™</sup></div>
                  <div className="mt-1 text-sm font-medium text-zinc-400">Every Run Tells A Story.</div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <h1 className="mt-8 font-display font-black text-[clamp(2.5rem,6vw,5rem)] leading-[0.98] tracking-tighter">
                Beyond Scores.<br />Beyond Statistics.<br />
                <span className="text-gradient-gold">Every Run Tells A Story.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-8 max-w-xl text-lg text-zinc-300 leading-relaxed">
                CricStoryLive™ is an AI-powered digital cricket platform that goes beyond live scores to
                celebrate every player, every match, every milestone, and every unforgettable cricket journey.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-4 max-w-xl text-zinc-400 leading-relaxed">
                From local playgrounds to international stadiums, we believe every cricketer deserves
                recognition, every performance deserves appreciation, and every run deserves a story.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="#why-cricstorylive"
                  data-testid="cricstory-cta-explore"
                  className="inline-flex items-center gap-2 rounded-full bg-yellow-500 text-black px-7 py-4 text-sm font-bold hover:bg-yellow-400 transition shadow-[0_10px_40px_rgba(212,175,55,0.4)] hover:-translate-y-0.5"
                >
                  Explore CricStoryLive <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/contact"
                  data-testid="cricstory-cta-community"
                  className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-7 py-4 text-sm font-medium hover:bg-white/10 transition backdrop-blur"
                >
                  Join the Cricket Community
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden ring-1 ring-yellow-500/20 shadow-[0_20px_80px_rgba(212,175,55,0.15)]">
                  <img src={ASSETS.images.cricstoryHero} alt="Cricket" className="h-full w-full object-cover" />
                </div>
                <motion.div
                  animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity }}
                  className="absolute -left-6 top-10 rounded-2xl bg-zinc-950 border border-yellow-500/30 shadow-xl p-4"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300">AI Story</div>
                  <div className="text-sm font-semibold mt-1 max-w-[180px]">"A debut fifty under lights — a star is born."</div>
                </motion.div>
                <motion.div
                  animate={{ y: [0, 8, 0] }} transition={{ duration: 5, repeat: Infinity }}
                  className="absolute -right-6 bottom-10 rounded-2xl bg-zinc-950 border border-yellow-500/30 shadow-xl p-4"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300">Live</div>
                  <div className="font-display text-2xl font-black mt-1">184/3 <span className="text-sm text-zinc-400 font-medium">(16.2)</span></div>
                  <div className="text-xs text-yellow-400 font-semibold mt-1">Ball-by-ball updates</div>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 1 — WHY CRICSTORYLIVE (COMPARISON) */}
      <section id="why-cricstorylive" className="relative py-24 border-t border-white/5" data-testid="cricstory-comparison">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Overline color="text-yellow-400">Why CricStoryLive?</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Not just scores. <span className="text-gradient-gold">A cricket ecosystem.</span>
            </h2>
          </div>

          <div className="mt-14 rounded-3xl overflow-hidden border border-white/10">
            <div className="grid grid-cols-2 bg-zinc-950">
              <div className="p-5 md:p-6 text-center font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 border-r border-white/10">Traditional Cricket Platforms</div>
              <div className="p-5 md:p-6 text-center font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-yellow-300 bg-yellow-500/10">CricStoryLive™</div>
            </div>
            {comparison.map((row, i) => (
              <Reveal key={row.neu} delay={i * 0.04}>
                <div className={`grid grid-cols-2 ${i % 2 === 0 ? "bg-zinc-900/60" : "bg-zinc-950/60"}`}>
                  <div className="p-4 md:p-5 flex items-center gap-3 border-r border-white/10">
                    {row.old ? (
                      <>
                        <X className="h-4 w-4 text-zinc-600 shrink-0" />
                        <span className="text-sm md:text-base text-zinc-500">{row.old}</span>
                      </>
                    ) : (
                      <span className="text-sm text-zinc-700 font-mono">—</span>
                    )}
                  </div>
                  <div className="p-4 md:p-5 flex items-center gap-3 bg-yellow-500/[0.04]">
                    <Check className="h-4 w-4 text-yellow-400 shrink-0" />
                    <span className="text-sm md:text-base font-semibold text-white">{row.neu}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — VISION */}
      <section className="relative py-28 border-t border-white/5 bg-zinc-950 overflow-hidden" data-testid="cricstory-vision">
        <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-yellow-500/10 blur-[120px]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Overline color="text-yellow-400">Our Vision</Overline>
          <Reveal>
            <h2 className="mt-6 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[1.02]">
              Cricket Creates Heroes.<br />
              <span className="text-gradient-gold">We Tell Their Stories.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap justify-center gap-2">
              {visionMoments.map((m) => (
                <span key={m} className="rounded-full bg-white/5 border border-yellow-500/20 px-4 py-2 text-sm font-medium text-zinc-300">
                  {m}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-8 font-display text-xl md:text-2xl font-bold text-yellow-300">
              ...deserves to be remembered.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-xl mx-auto text-zinc-400 leading-relaxed">
              CricStoryLive exists to celebrate the human stories behind cricket.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 3 — MORE THAN LIVE SCORES */}
      <section className="relative py-24 border-t border-white/5" data-testid="cricstory-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-yellow-400">More Than Live Scores</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Four pillars. <span className="text-gradient-gold">One ecosystem.</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08}>
                <div className="group rounded-3xl border border-yellow-500/15 bg-white/[0.03] backdrop-blur p-7 h-full hover:bg-yellow-500/[0.06] hover:border-yellow-500/30 hover:-translate-y-1 transition">
                  <div className="h-12 w-12 rounded-2xl bg-yellow-500/10 border border-yellow-500/30 grid place-items-center text-yellow-400">
                    <p.i className="h-6 w-6" />
                  </div>
                  <div className="mt-5 font-display text-xl font-black">{p.t}</div>
                  <ul className="mt-4 space-y-2.5">
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — DISCOVER TOMORROW'S STARS */}
      <section className="relative py-24 border-t border-white/5 bg-zinc-950" data-testid="cricstory-grassroots">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Overline color="text-yellow-400">Discover Tomorrow's Cricket Stars</Overline>
            <h2 className="mt-4 font-display font-black text-4xl tracking-tighter leading-tight">
              Every player deserves a <span className="text-gradient-gold">cricket identity.</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Millions of talented cricketers play every day without recognition. CricStoryLive is building a
              digital platform where every player — from school cricket to international cricket — can create
              a lasting cricket identity.
            </p>
            <p className="mt-4 text-sm text-zinc-500">
              Aligned with the PJSRS mission of empowering talent. <span className="text-yellow-400/80 font-medium">Inspire. Empower. Entertain.</span>
            </p>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-2.5">
            {grassroots.map((g, i) => (
              <Reveal key={g} delay={i * 0.04}>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-yellow-500/20 px-5 py-3 text-sm font-semibold text-zinc-200 hover:border-yellow-500/50 hover:bg-yellow-500/10 transition">
                  <Trophy className="h-4 w-4 text-yellow-400" /> {g}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — AI-POWERED EXPERIENCE */}
      <section className="relative py-24 border-t border-white/5" data-testid="cricstory-ai">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-yellow-400">AI-Powered Cricket Experience</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Intelligence that <span className="text-gradient-gold">amplifies emotion.</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Artificial Intelligence enhances the experience while preserving the excitement and emotion of
              cricket.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {aiFeatures.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.04}>
                <div className="group rounded-2xl border border-yellow-500/15 bg-gradient-to-br from-yellow-500/[0.04] to-transparent p-6 text-center hover:from-yellow-500/[0.12] hover:-translate-y-1 transition h-full">
                  <div className="h-12 w-12 rounded-xl mx-auto bg-yellow-500/10 border border-yellow-500/30 grid place-items-center text-yellow-400 group-hover:scale-110 transition-transform">
                    <f.i className="h-5 w-5" />
                  </div>
                  <div className="mt-4 text-sm font-semibold text-white">{f.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — ECOSYSTEM */}
      <section className="relative py-24 border-t border-white/5 bg-zinc-950 overflow-hidden" data-testid="cricstory-ecosystem">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-64 w-[500px] rounded-full bg-yellow-500/10 blur-[100px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Overline color="text-yellow-400">The CricStoryLive Ecosystem</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Connecting everyone <span className="text-gradient-gold">in cricket.</span>
            </h2>
          </div>

          {/* Desktop circular ecosystem */}
          <div className="hidden lg:block relative mx-auto mt-16 h-[620px] w-[620px]">
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
              {ecosystemNodes.map((n, i) => {
                const angle = (i / ecosystemNodes.length) * 2 * Math.PI - Math.PI / 2;
                const x = 50 + 45 * Math.cos(angle);
                const y = 50 + 45 * Math.sin(angle);
                return (
                  <motion.line
                    key={n.t}
                    x1="50" y1="50" x2={x} y2={y}
                    stroke="rgba(212,175,55,0.3)" strokeWidth="0.3" strokeDasharray="1.5 1.2"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.2 + i * 0.06 }}
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <motion.div
                animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 3, repeat: Infinity }}
                className="h-40 w-40 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 grid place-items-center text-center text-black shadow-[0_0_60px_rgba(212,175,55,0.45)]"
              >
                <div className="px-3">
                  <Sparkles className="h-6 w-6 mx-auto" />
                  <div className="mt-1 font-display font-black text-lg leading-tight">CricStoryLive™</div>
                </div>
              </motion.div>
            </div>
            {ecosystemNodes.map((n, i) => {
              const angle = (i / ecosystemNodes.length) * 2 * Math.PI - Math.PI / 2;
              const x = 50 + 45 * Math.cos(angle);
              const y = 50 + 45 * Math.sin(angle);
              return (
                <Reveal key={n.t} delay={0.3 + i * 0.05}>
                  <div
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 w-24 text-center"
                    style={{ left: `${x}%`, top: `${y}%` }}
                  >
                    <div className="h-11 w-11 rounded-2xl bg-zinc-950 border border-yellow-500/30 grid place-items-center text-yellow-400 shadow-lg">
                      <n.i className="h-5 w-5" />
                    </div>
                    <div className="text-[11px] font-semibold leading-snug">{n.t}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Mobile ecosystem */}
          <div className="lg:hidden mt-12">
            <div className="mx-auto h-28 w-28 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 grid place-items-center text-center text-black shadow-[0_0_50px_rgba(212,175,55,0.4)]">
              <div className="px-2">
                <Sparkles className="h-5 w-5 mx-auto" />
                <div className="mt-0.5 font-display font-black text-xs leading-tight">CricStoryLive™</div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {ecosystemNodes.map((n) => (
                <div key={n.t} className="flex items-center gap-3 rounded-2xl border border-yellow-500/20 bg-zinc-900/80 p-4">
                  <n.i className="h-5 w-5 text-yellow-400 shrink-0" />
                  <span className="text-sm font-semibold">{n.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7 — EVERY RUN CREATES MEMORIES */}
      <section className="relative py-28 border-t border-white/5" data-testid="cricstory-memories">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Overline color="text-yellow-400">Every Run Creates Memories</Overline>
          <div className="mt-10 space-y-4">
            {memories.map((m, i) => (
              <Reveal key={m} delay={i * 0.08}>
                <p className="font-display text-xl md:text-2xl font-bold text-zinc-200">
                  {m}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.55}>
            <p className="mt-10 font-display text-2xl md:text-3xl font-black text-gradient-gold">
              That story deserves to be remembered.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 8 — ROADMAP */}
      <section className="relative py-24 border-t border-white/5 bg-zinc-950" data-testid="cricstory-roadmap">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-yellow-400">Future Roadmap</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              What's <span className="text-gradient-gold">next.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {phases.map((ph, i) => (
              <Reveal key={ph.p} delay={i * 0.08}>
                <div className="rounded-3xl border border-yellow-500/15 bg-white/[0.03] p-7 h-full">
                  <div className="inline-flex items-center gap-2 rounded-full bg-yellow-500 text-black px-4 py-1.5 text-xs font-bold">
                    {ph.p}
                  </div>
                  <ul className="mt-5 space-y-2.5">
                    {ph.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — WHY CHOOSE */}
      <section className="relative py-24 border-t border-white/5" data-testid="cricstory-why-choose">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Overline color="text-yellow-400">Why CricStoryLive?</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Everything cricket <span className="text-gradient-gold">deserves.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-4">
            {whyChoose.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.04}>
                <div className="group rounded-2xl border border-yellow-500/15 bg-white/[0.03] p-6 text-center hover:border-yellow-500/40 hover:-translate-y-1 transition h-full">
                  <div className="h-12 w-12 rounded-xl mx-auto bg-yellow-500/10 border border-yellow-500/30 grid place-items-center text-yellow-400">
                    <w.i className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-display font-bold text-white text-sm">{w.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10 — BUILT FOR EVERY CRICKET LOVER */}
      <section className="relative py-24 border-t border-white/5 bg-zinc-950" data-testid="cricstory-built-for">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Overline color="text-yellow-400">Built for Every Cricket Lover</Overline>
          <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
            Whether you are<span className="text-gradient-gold">...</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-2.5">
            {builtFor.map((b, i) => (
              <Reveal key={b} delay={i * 0.04}>
                <span className="rounded-full bg-white/5 border border-yellow-500/20 px-5 py-3 text-sm font-semibold text-zinc-200 hover:border-yellow-500/50 transition">
                  {b}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.4}>
            <p className="mt-10 font-display text-xl md:text-2xl font-bold text-yellow-300">
              CricStoryLive is designed for you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL BANNER */}
      <section className="relative py-28 overflow-hidden" data-testid="cricstory-final-banner">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-600 via-amber-500 to-yellow-500" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center text-black">
          <Quote className="h-8 w-8 mx-auto opacity-70" />
          <h2 className="mt-6 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[1.02]">
            Every Run Tells A Story.<br />Every Player Inspires.<br />Every Match Matters.
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-black/80 text-lg font-medium">
            Join us in building the world's most inspiring AI-powered cricket platform.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              data-testid="cricstory-banner-cta"
              className="inline-flex items-center gap-2 rounded-full bg-black text-yellow-400 px-8 py-4 text-sm font-bold hover:bg-zinc-900 transition shadow-xl"
            >
              Join the Cricket Community <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/products"
              data-testid="cricstory-banner-products"
              className="inline-flex items-center gap-2 rounded-full bg-white/30 text-black border border-black/20 px-8 py-4 text-sm font-medium hover:bg-white/50 transition backdrop-blur"
            >
              Explore other products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
